'use client';

import { Copy, Edit3, ExternalLink, Link, MoreHorizontal, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';

import { cancelAnchorNavigation } from '@/shared/utils/events';

import { cn } from '@/utils/styles';

const FormDropdownMenu = ({
	isDraft,
	isDropdownOpen,
	setIsDropdownOpen,
	handleDuplicate,
	handleDelete,
	handleEdit,
	handleViewForm,
	handleCopyLink,
	formStatus,
	handleToggleAcceptingApplications,
	isUpdatingStatus,
}: {
	isDraft: boolean;
	isDropdownOpen: boolean;
	setIsDropdownOpen: (open: boolean) => void;
	handleDuplicate: () => void;
	handleDelete: (e: React.MouseEvent | React.KeyboardEvent) => void;
	handleEdit: () => void;
	handleViewForm: (e: React.MouseEvent | React.KeyboardEvent) => void;
	handleCopyLink: (e: React.MouseEvent | React.KeyboardEvent) => void;
	formStatus: string;
	handleToggleAcceptingApplications: (checked: boolean) => void;
	isUpdatingStatus: boolean;
}) => {
	const isAccepting = formStatus === 'PUBLISHED';

	return (
		<DropdownMenu
			open={isDropdownOpen}
			onOpenChange={setIsDropdownOpen}
		>
			<DropdownMenuTrigger asChild>
				<Button
					variant="light"
					size="sm"
					className={cn('text-muted-foreground hover:text-primary group-hover:text-secondary')}
					onClick={(e) => {
						cancelAnchorNavigation(e);
						setIsDropdownOpen(!isDropdownOpen);
					}}
				>
					<MoreHorizontal className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuPortal container={document.getElementById('popovers')}>
				<DropdownMenuContent
					className="min-w-[220px]"
					align="end"
					side="bottom"
					onClick={(e) => e.stopPropagation()}
				>
					{isDraft ? null : (
						<>
							{/* View form */}
							<DropdownMenuItem
								className="flex cursor-pointer gap-2"
								onClick={handleViewForm}
							>
								<ExternalLink className="h-4 w-4" />
								View form
							</DropdownMenuItem>

							{/* Copy link */}
							<DropdownMenuItem
								className="flex cursor-pointer gap-2"
								onClick={handleCopyLink}
							>
								<Link className="h-4 w-4" />
								Copy link
							</DropdownMenuItem>
						</>
					)}

					{/* Edit form */}
					<DropdownMenuItem
						className="flex cursor-pointer gap-2"
						onClick={handleEdit}
					>
						<Edit3 className="h-4 w-4" />
						Edit form
					</DropdownMenuItem>

					{/* Duplicate form */}
					<DropdownMenuItem
						className="flex cursor-pointer gap-2"
						onClick={handleDuplicate}
					>
						<Copy className="h-4 w-4" />
						Duplicate form
					</DropdownMenuItem>

					{/* Delete form */}
					<DropdownMenuItem
						className="flex cursor-pointer gap-2 text-destructive focus:text-destructive"
						onClick={handleDelete}
					>
						<Trash2 className="h-4 w-4" />
						Delete form
					</DropdownMenuItem>

					{/* Accepting applications toggle - mobile only */}
					{!isDraft && (
						<div className="md:hidden">
							<DropdownMenuSeparator />
							<div
								role="presentation"
								className="flex items-center justify-between gap-6 px-3 py-2.5"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
								}}
								onMouseDown={(e) => e.stopPropagation()}
							>
								<span className="text-sm font-medium">
									{isAccepting ? 'Accepting submissions' : 'Not accepting submissions'}
								</span>
								<Switch
									className="h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input [&>span]:h-4 [&>span]:w-4 [&>span]:data-[state=checked]:translate-x-4"
									checked={isAccepting}
									onCheckedChange={handleToggleAcceptingApplications}
									disabled={isUpdatingStatus}
								/>
							</div>
						</div>
					)}
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	);
};

export { FormDropdownMenu };
